/**
 * Ficha do piloto: o estado numa frase e quatro mosaicos (médico, licença, revalidação da PU ou instrução prática, último voo).
 *
 * Tudo vem do que o servidor já calcula para cada piloto (`operational_state`, `pu_revalidation`, `flight_stats`,
 * `student_progress`): nunca se usa o relógio do navegador (os "dias" vêm do servidor).
 *
 * Tons: "ok" (verde), "warn" (âmbar), "bad" (vermelho), "info" (progresso em curso, sem problema) e "neutral" (só informa).
 * Só os que falham ou estão a acabar ganham cor; o resto fica discreto.
 */
import { fmtDMY, fmtHM } from "./instructorView";

// Nome de cada tipo de licença no ecrã (a chave é o que está guardado na base de dados)
export const LICENSE_LABEL = { PU: "PU", PPL: "PPL", outro: "outro", cartao_aluno: "Cartão de Aluno" };

const WARN_DAYS = 45;              // mesma janela âmbar do servidor
const REVALIDATE_WARN_DAYS = 90;

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const daysWord = (n) => plural(n, "dia", "dias");

export const KIND_LABEL = { regular: "Piloto regular", student: "Aluno", avulso: "Piloto avulso" };

// Número provisório: o sistema gera um código interno (ALN-…) para o aluno até a ANAC emitir o cartão de aluno.
// Esse código não é um número válido: não se mostra, e o formulário fica em branco até ser escrito o número real.
export const isNumeroProvisorio = (pu) => typeof pu === "string" && pu.startsWith("ALN-");
export const numeroVisivel = (pu) => (isNumeroProvisorio(pu) ? "" : pu || "");

/** Tom de uma validade: vermelho se já passou ou falta registo, âmbar nos últimos 45 dias. */
function validityTone(days, hasDate) {
  if (!hasDate || days == null) return "bad";
  if (days < 0) return "bad";
  if (days <= WARN_DAYS) return "warn";
  return "ok";
}

function validityTile(key, label, short, crit, reasonLabel, reasonOf) {
  const d = crit.days_remaining;
  const tone = validityTone(d, !!crit.valid_until);
  if (tone === "bad") reasonOf[key] = !crit.valid_until ? `${reasonLabel}: sem validade registada` : `${reasonLabel} expirou há ${daysWord(-d)}`;
  else if (tone === "warn") reasonOf[key] = `${reasonLabel} acaba em ${daysWord(d)}`;
  return {
    key, label, short, tone,
    value: crit.valid_until ? fmtDMY(crit.valid_until) : "sem registo",
    sub: !crit.valid_until ? "falta registar a validade" : d < 0 ? `expirou há ${daysWord(-d)}` : `${daysWord(d)} restantes`,
  };
}

/** Frase do último voo: "hoje", "ontem" ou "há N dias" (os dias vêm do servidor). */
function lastFlightValue(days) {
  if (days == null) return "Sem voos";
  if (days <= 0) return "hoje";
  if (days === 1) return "ontem";
  return `há ${days} dias`;
}

/** A revalidação da PU (3 h e 10 aterragens nos 6 meses antes da validade) em palavras: valor, sub, tom, progresso. */
function revalidationOf(rev, licDays) {
  if (rev.window_phase === "no_validity") {
    return { value: "—", sub: "sem validade da licença", tone: "neutral", progress: "", pct: null };
  }
  if (rev.window_phase === "not_open") {
    return { value: "Ainda não abriu", sub: `abre a ${fmtDMY(rev.window_start)}`, tone: "ok", progress: `A janela abre a ${fmtDMY(rev.window_start)}`, pct: null };
  }
  const landings = rev.landings_count ?? rev.flights_count;
  const have = `${fmtHM(rev.total_minutes)} e ${plural(landings, "aterragem", "aterragens")}`;
  const pct = Math.min(100, Math.round(100 * Math.min(rev.total_minutes / rev.required_minutes, landings / rev.required_flights)));
  if (rev.state === "apto") {
    return { value: "Cumprida", sub: `tem ${have}`, tone: "ok", progress: `Já cumpre: ${have}`, pct };
  }
  const miss = [];
  if (rev.missing_minutes > 0) miss.push(fmtHM(rev.missing_minutes));
  if (rev.missing_flights > 0) miss.push(plural(rev.missing_flights, "aterragem", "aterragens"));
  const confirm = rev.state === "confirmar_aterragens";
  // Com muito tempo pela frente é só progresso (azul); a aproximar-se do fim da validade passa a aviso e depois a erro
  const tone = licDays == null || licDays < 0 ? "bad" : licDays <= WARN_DAYS ? "bad" : licDays <= REVALIDATE_WARN_DAYS ? "warn" : "info";
  return {
    value: confirm ? "Confirmar aterragens" : "Por fazer",
    sub: `tem ${have}: faltam ${miss.join(" e ")}${confirm ? " (ou confirmar as aterragens)" : ""}`,
    tone,
    progress: `Tem ${have}: faltam ${miss.join(" e ")}${confirm ? " (ou confirmar as aterragens)" : ""}`,
    pct,
  };
}

export function buildPilotView(p) {
  const state = p?.operational_state || {};
  const c = state.criteria || {};
  const med = c.medical || {};
  const lic = c.license || {};
  const stats = p?.flight_stats || {};
  const rev = p?.pu_revalidation || null;
  const sp = p?.student_progress || null;
  const kind = p?.kind === "student" ? "student" : p?.kind === "avulso" ? "avulso" : "regular";
  const rawType = lic.license_type || p?.license_type || "PU";
  const licType = LICENSE_LABEL[rawType] || rawType;
  const licApplicable = lic.applicable !== false;
  const isInactive = p?.active === false;

  const reasonOf = {};
  const tiles = [];

  tiles.push(validityTile("medical", "Certificado médico", "Cert. médico", med, "Certificado médico", reasonOf));

  if (licApplicable) {
    tiles.push(validityTile("license", `Licença ${licType}`, `Licença ${licType}`, lic, `Licença ${licType}`, reasonOf));
  } else {
    tiles.push({ key: "license", label: "Licença", short: "Licença", tone: "neutral", value: "Ainda é aluno", sub: "sem licença registada" });
  }

  // Revalidação da PU (pilotos regulares) ou instrução prática (alunos)
  let revView = null;
  if (kind === "student" && sp) {
    const dcMiss = Math.max(0, sp.dc_target_minutes - sp.dc_minutes);
    const soloMiss = Math.max(0, sp.solo_target_minutes - sp.solo_minutes);
    const miss = [];
    if (dcMiss > 0) miss.push(`${fmtHM(dcMiss)} de DC`);
    if (soloMiss > 0) miss.push(`${fmtHM(soloMiss)} de solo`);
    tiles.push({
      key: "instruction", label: "Instrução prática para a prova", short: "Instrução prática", tone: sp.ready ? "ok" : "info",
      value: `${fmtHM(sp.dc_minutes)} de ${fmtHM(sp.dc_target_minutes)}`,
      sub: sp.ready ? "apto para a prova" : `faltam ${miss.join(" e ")}`,
    });
    reasonOf.instruction = sp.ready ? "Apto para a prova" : `Faltam ${miss.join(" e ")} para a prova`;
  } else if (rev) {
    revView = revalidationOf(rev, lic.days_remaining);
    tiles.push({ key: "revalidation", label: `Revalidação da ${licType}`, short: `Revalidação ${licType}`, tone: revView.tone, value: revView.value, sub: revView.sub });
    if ((revView.tone === "bad" || revView.tone === "warn") && lic.days_remaining != null && lic.days_remaining >= 0) {
      reasonOf.revalidation = `Revalidação da ${licType} por fazer (acaba em ${daysWord(lic.days_remaining)})`;
    }
  }

  const trained = (stats.flights || 0) > 0 && stats.days_since_last_flight != null;
  tiles.push({
    key: "last_flight", label: "Último voo", short: "Último voo", tone: "neutral",
    value: trained ? lastFlightValue(stats.days_since_last_flight) : "Sem voos",
    sub: trained ? `${plural(stats.flights, "voo", "voos")} · DC ${fmtHM(stats.dc_minutes)}` : "ainda sem voos registados",
  });

  // ── Estado numa frase ──────────────────────────────────────────────────────
  const badKeys = ["medical", "license"].filter((k) => tiles.find((t) => t.key === k)?.tone === "bad");
  const warnKeys = new Set();
  if (tiles.find((t) => t.key === "medical")?.tone === "warn") warnKeys.add("medical");
  // A licença a acabar e a revalidação por fazer são a mesma causa: contam um aviso
  if (tiles.find((t) => t.key === "license")?.tone === "warn" || (reasonOf.revalidation && !badKeys.includes("license"))) warnKeys.add("license");
  const warnList = ["medical", "license"].filter((k) => warnKeys.has(k));
  const reasonFor = (k) => reasonOf[k] || (k === "license" ? reasonOf.revalidation : null) || "Ver detalhes";

  let phrase;
  let reason;
  let tone;
  if (isInactive) {
    tone = "inactive"; phrase = "Desativado"; reason = "Piloto desativado: não conta para os avisos";
  } else if (state.operacional === false || badKeys.length > 0) {
    tone = "grounded"; phrase = "Não operacional";
    const first = badKeys[0];
    reason = (first ? reasonFor(first) : state.motivo || "Ver detalhes") + (badKeys.length > 1 ? ` (+${badKeys.length - 1})` : "");
  } else if (warnList.length > 0) {
    tone = "warn"; phrase = `Operacional, com ${plural(warnList.length, "aviso", "avisos")}`;
    reason = reasonFor(warnList[0]) + (warnList.length > 1 ? ` (+${warnList.length - 1})` : "");
  } else {
    tone = "ok"; phrase = "Operacional";
    reason = kind === "student" && reasonOf.instruction ? reasonOf.instruction : "Tudo em dia";
  }

  // ── Horas (ao abrir) ─────────────────────────────────────────────────────────
  const metrics = [
    { label: kind === "student" ? "Solo" : "Voos próprios (comando)", value: fmtHM(stats.solo_minutes) },
    { label: "Duplo comando", value: fmtHM(stats.dc_minutes) },
    { label: "Voos", value: String(stats.flights || 0) },
    { label: "Último voo", value: stats.last_flight ? fmtDMY(String(stats.last_flight).slice(0, 10)) : "—" },
  ];

  // Instrução prática do aluno: duas barras (duplo comando e solo)
  const bar = (label, have, need) => ({ label, have: fmtHM(have), need: fmtHM(need), pct: Math.min(100, Math.round((100 * have) / (need || 1))), ok: have >= need });
  const student = kind === "student" && sp
    ? { ready: !!sp.ready, bars: [bar("Duplo comando (DC)", sp.dc_minutes, sp.dc_target_minutes), bar("Solo", sp.solo_minutes, sp.solo_target_minutes)] }
    : null;

  // ── Revalidações (uma linha cada) ────────────────────────────────────────────
  const revalidations = [];
  if (licApplicable) {
    const days = lic.days_remaining;
    const licTone = validityTone(days, !!lic.valid_until);
    const isPu = !!rev;
    // A linha fica vermelha se a licença falhou; âmbar/vermelha se a revalidação já preocupa; senão segue a validade
    const revProblem = !!revView && (revView.tone === "bad" || revView.tone === "warn");
    revalidations.push({
      key: "license", title: `Licença ${licType}`,
      tone: licTone === "bad" ? "bad" : revProblem ? revView.tone : licTone,
      rule: !lic.valid_until
        ? "Sem validade registada: não se sabe quando revalidar"
        : isPu ? `3 h e 10 aterragens nos 6 meses antes de ${fmtDMY(lic.valid_until)}` : `Renovar a licença antes de ${fmtDMY(lic.valid_until)}`,
      progress: revView ? revView.progress : "", pct: revView ? revView.pct : null,
      days, validUntil: lic.valid_until, target: "edit",
    });
  }
  revalidations.push({
    key: "medical", title: "Certificado médico", tone: validityTone(med.days_remaining, !!med.valid_until),
    rule: med.valid_until ? `Renovar no médico antes de ${fmtDMY(med.valid_until)}` : "Sem validade registada", progress: "", pct: null,
    days: med.days_remaining, validUntil: med.valid_until, target: "edit",
  });

  // ── Linha do tempo (próximos 12 meses): só o que tem data ─────────────────────
  const timeline = revalidations
    .filter((r) => r.validUntil && r.days != null && r.days <= 365)
    .map((r) => ({
      key: r.key, days: Math.max(0, r.days), overdue: r.days < 0, tone: r.tone, date: r.validUntil,
      label: r.key === "medical" ? "Médico" : licType,
      detail: `${r.title}: ${r.days < 0 ? `expirou há ${daysWord(-r.days)}` : `a ${fmtDMY(r.validUntil)} (${daysWord(r.days)})`}`,
    }))
    .sort((a, b) => a.days - b.days);

  return {
    kind: tone, pilotKind: kind, phrase, reason, tiles, metrics, student, revalidations, timeline,
    info: {
      medicalNumber: p?.medical_cert_number || null,
      license: `${licType} ${p?.pu_number || ""}`.trim(),
      kindLabel: KIND_LABEL[kind],
      notes: p?.notes || "",
    },
    attentionCount: warnList.length,
  };
}
